// @ts-nocheck
import {create} from "zustand";
import {listGamesService} from "../services/games/gamesList/listGames.service";
import {listMyFavoriteGamesService} from "../services/games/miniGames/favoriteGames.service";
import {useAuthStore} from "./useAuth.store";

/**
 * Hook specifically for home page components that need access to complete games list
 * for metadata lookup (icons, names, etc.) without being affected by filters
 */
export const useGameMetadataStore = create((set, get) => ({
  // Complete unfiltered games list for metadata lookup
  allGames: [],
  loading: false,
  error: null,
  initialized: false,

  // Initialize the games list - should only be called once
  initializeGames: async () => {
    const {initialized, loading} = get();

    // Prevent multiple simultaneous calls
    if (initialized || loading) return;

    set({loading: true, error: null});

    try {
      const authStatus = useAuthStore.getState().authStatus;
      let favGames = [];

      // Fetch all games without any filters
      const response = await listGamesService();

      // Construct the complete games list
      const allGames = response.data.data.providers.reduce((acc, item) => {
        item.games.forEach(game => {
          acc.push(game);
        });
        return acc;
      }, []);

      // Add favorite status if user is authenticated
      if (authStatus === "authenticated") {
        try {
          const favGamesResp = await listMyFavoriteGamesService();
          favGames = favGamesResp?.data?.data || [];

          allGames.forEach(game => {
            const favGame = favGames.some(item => item.game_id === game.id && item.game_provider === game.provider);
            game.isFav = !!favGame;
          });
        } catch (_favError) {
          // Continue without favorite status
        }
      }

      set({
        allGames,
        loading: false,
        initialized: true,
        error: null,
      });
    } catch (error) {
      set({
        loading: false,
        error: {
          message: error.message || "Failed to load games",
          code: error.status || 500,
        },
      });
    }
  },

  getGameById: (gameId, provider = null) => {
    const {allGames} = get();

    if (provider) {
      return allGames.find(game => (game.id === gameId || game.id === String(gameId)) && game.provider === provider);
    }

    return allGames.find(game => game.id === gameId || game.id === String(gameId));
  },

  // Get game by name - normalized lookup for matching with external data
  getGameByName: gameName => {
    const {allGames} = get();

    const normalize = (str = "") =>
      str
        .toLowerCase()
        .trim()
        .replace(/[''`´]/g, "")
        .replace(/[^a-z0-9áéíóúüñ\s]/gi, "")
        .replace(/\s+/g, " ");

    const normalizedSearchName = normalize(gameName);

    return allGames.find(game => normalize(game.name) === normalizedSearchName);
  },

  // Create a Map for efficient lookups by name (useful for bulk operations)
  getGamesMap: () => {
    const {allGames} = get();

    const normalize = (str = "") =>
      str
        .toLowerCase()
        .trim()
        .replace(/[''`´]/g, "")
        .replace(/[^a-z0-9áéíóúüñ\s]/gi, "")
        .replace(/\s+/g, " ");

    return new Map(allGames.map(game => [normalize(game.name), game]));
  },

  // Reset the store (useful for auth state changes)
  reset: () => {
    set({
      allGames: [],
      loading: false,
      error: null,
      initialized: false,
    });
  },
}));
